import { useQueryDetermineLogin } from '@/hooks/query/useQueryDetermineLogin';
import { setAuthTypeUserProperty } from '@/lib/analytics/scribe/auth';
import { useDesktopAuthSafe } from '@/tauri/auth0/desktop-auth-context';
import { isTauri, isUsingReactKinde } from '@/tauri/utils';
import { isDetermineLoginAllowed } from '@/utils/auth/isDetermineLoginAllowed';
import { logger } from '@/utils/logger/logger';
import { setScreenHint } from '@/utils/signInSessionTracking';
import { useTranslations } from '@/utils/translations';
import { AUTH0_DB_CONNECTION, AUTH0_LOGIN_PATH } from '@heidi/auth/contracts';
import {
  AuthenticationMethod,
  AuthVendor,
  type ScreenHint,
} from '@heidi/schema/ml-scribe';
import { toAuth0UiLocales } from '@heidi/utils/i18n';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { useCallback } from 'react';

import { iframeSafeAuthRoute } from '../utils';

interface UseDetermineLoginSubmitParams {
  email: string;
  postLoginRedirectUri: string;
  emailConnectionId: string;
  /**
   * Overrides the locale used for Auth0 `ui_locales` / Kinde `lang`. Pre-auth
   * desktop flows (dictate onboarding) must pass the react-intl locale here:
   * `useTranslations().locale` falls back to `router.locale` (always `en` on
   * the desktop static export), so without this the auth page ignores the OS
   * locale even when the UI is localized.
   */
  localeOverride?: string;
  /**
   * Extra query parameters appended to the Auth0 web login URL, which the Auth0
   * route forwards to `/authorize`. Not sent on the Kinde routes, and not
   * carried by the desktop login request, which has no field for them.
   */
  extraAuthorizeParams?: Record<string, string>;
}

export function useEmailLoginSubmit({
  email,
  postLoginRedirectUri,
  emailConnectionId,
  localeOverride,
  extraAuthorizeParams,
}: UseDetermineLoginSubmitParams) {
  const { locale } = useTranslations({ localeOverride });
  const { register: kindeRegister } = useKindeAuth();
  const { login: desktopAuthLogin } = useDesktopAuthSafe();
  const shouldUseDesktopAuth = isTauri();
  const usingReactKinde = isUsingReactKinde();

  const { refetch: refetchDetermineLogin, isFetching: isDetermineLoginFetching } =
    useQueryDetermineLogin(email);

  const isDetermineLoginEnabled = isDetermineLoginAllowed();

  const buildAuth0LoginUrl = useCallback(
    (screenHint?: ScreenHint | null, connection?: string | null) => {
      const params = new URLSearchParams();
      params.set('returnTo', postLoginRedirectUri);
      params.set('login_hint', email);
      if (locale) {
        params.set('ui_locales', toAuth0UiLocales(locale));
      }
      if (screenHint) {
        params.set('screen_hint', screenHint);
      }
      if (connection) {
        params.set('connection', connection);
      }
      // Force Auth0 to show the login screen instead of silently re-using an
      // existing session. Without this, a stale Auth0 session can cause the
      // user to be authenticated as a different account than the one they entered.
      if (connection === AUTH0_DB_CONNECTION) {
        params.set('prompt', 'login');
      }
      if (extraAuthorizeParams) {
        for (const [key, value] of Object.entries(extraAuthorizeParams)) {
          // Extras only add; they never replace a parameter this hook owns.
          if (!params.has(key)) params.set(key, value);
        }
      }
      return `${AUTH0_LOGIN_PATH}?${params.toString()}`;
    },
    [postLoginRedirectUri, email, locale, extraAuthorizeParams]
  );

  const routeToAuth0Login = useCallback(
    (screenHint?: ScreenHint | null, connection?: string | null): void => {
      if (shouldUseDesktopAuth) {
        void desktopAuthLogin({
          prompt: 'login',
          loginHint: email,
          uiLocales: toAuth0UiLocales(locale),
          ...(screenHint && { screenHint }),
          ...(connection && { connection }),
        }).catch((error) => {
          logger.error('[Auth0] Desktop login failed', error);
        });
        return;
      }

      const loginUrl = buildAuth0LoginUrl(screenHint, connection);
      iframeSafeAuthRoute(loginUrl);
    },
    [shouldUseDesktopAuth, desktopAuthLogin, email, locale, buildAuth0LoginUrl]
  );

  const routeToKindeLogin = useCallback(() => {
    if (usingReactKinde) {
      void kindeRegister({
        authUrlParams: {
          login_hint: email,
          post_login_redirect_url: postLoginRedirectUri,
          connection_id: emailConnectionId,
          lang: locale,
        },
        app_state: {
          redirectTo: postLoginRedirectUri,
        },
      });
    } else {
      const url = new URL('/api/auth/register', window.location.origin);
      url.searchParams.set('login_hint', email);
      url.searchParams.set('post_login_redirect_url', postLoginRedirectUri);
      url.searchParams.set('connection_id', emailConnectionId);
      iframeSafeAuthRoute(url.toString());
    }
  }, [
    usingReactKinde,
    kindeRegister,
    email,
    postLoginRedirectUri,
    emailConnectionId,
    locale,
  ]);

  const submitViaDetermineLogin = useCallback(async () => {
    const result = await refetchDetermineLogin();

    if (result.isError || !result.data) {
      logger.warn('[Identity Broker] determine-login failed, falling back to Kinde', {
        error: result.error,
      });
      routeToKindeLogin();
      return;
    }

    const { vendor, screen_hint, connection, authentication_method } = result.data;

    logger.info('[Identity Broker] Routing decision', {
      vendor,
      screen_hint,
      connection,
      route_target: vendor === AuthVendor.auth0 ? 'auth0' : 'kinde',
    });

    setScreenHint(screen_hint ?? 'signin');

    if (authentication_method === AuthenticationMethod.SSO) {
      setAuthTypeUserProperty('sso');
    }

    if (vendor === AuthVendor.auth0) {
      routeToAuth0Login(screen_hint, connection);
    } else {
      routeToKindeLogin();
    }
  }, [refetchDetermineLogin, routeToAuth0Login, routeToKindeLogin]);

  return {
    routeToKindeLogin,
    routeToAuth0Login,
    isDetermineLoginFetching,
    isDetermineLoginEnabled,
    submitViaDetermineLogin,
  };
}
